import type { Organization } from '@engagement-web-app/common';
import { useMutation } from '@tanstack/react-query';
import { Flex } from '@/components/Flex';
import { Skeleton } from '@/components/Skeleton';
import { Switch } from '@/components/Switch';
import { showErrorToast, showSuccessToast } from '@/components/Toasts';
import { useSystemPermission } from '@/permissions/SystemPermissionProvider';
import { toggleOrganizationMfa } from '@/queries/organizations';
import * as styles from './SecuritySection.css';

interface SecuritySectionProps {
    organization: Organization;
    onMfaToggled: (mfaEnabled: boolean) => void;
}

export function SecuritySection({ organization, onMfaToggled }: SecuritySectionProps) {
    const { can } = useSystemPermission();
    const canToggleMfa = can('organization', 'mfaToggle');

    const toggleMfaMutation = useMutation({
        mutationFn: (enabled: boolean) => toggleOrganizationMfa(organization.uuid, enabled),
        onSuccess(data) {
            onMfaToggled(data.mfaEnabled);
            showSuccessToast({
                title: 'セキュリティ設定',
                description: data.mfaEnabled ? '多要素認証が有効になりました。' : '多要素認証が無効になりました。',
            });
        },
        onError(error) {
            console.error('Failed to toggle MFA:', error);
            showErrorToast({
                title: 'セキュリティ設定',
                description: '多要素認証の設定変更に失敗しました。',
            });
        },
    });

    const handleToggle = (checked: boolean) => {
        toggleMfaMutation.mutate(checked);
    };

    return (
        <Flex gap="4" align="center">
            <Switch
                checked={organization.mfaEnabled}
                onCheckedChange={handleToggle}
                disabled={!canToggleMfa || toggleMfaMutation.isPending}
            />

            <Flex gap="1" direction="column">
                <span className={styles.mfaTitle}>多要素認証 (MFA)</span>
                <span className={styles.mfaDescription}>
                    多要素認証は組織内のすべてのユーザーに適用されます。
                    <br />
                    認証方法：認証アプリ (Google Authenticator, Microsoft Authenticator, Authyなど)
                </span>
            </Flex>
        </Flex>
    );
}

export function SecuritySectionSkeleton() {
    return (
        <Flex gap="4" align="center">
            <Skeleton.Box width={40} height={22} />

            <Flex gap="1" direction="column">
                <span className={styles.mfaTitle}>多要素認証 (MFA)</span>
                <span className={styles.mfaDescription}>
                    多要素認証は組織内のすべてのユーザーに適用されます。
                    <br />
                    認証方法：認証アプリ (Google Authenticator, Microsoft Authenticator, Authyなど)
                </span>
            </Flex>
        </Flex>
    );
}
